import { InputHTMLAttributes, ReactElement, useMemo, useCallback, KeyboardEvent } from 'react'
import { RefCallBack, useFormContext, useController } from 'react-hook-form'
import NumberFormat, { NumberFormatProps, NumberFormatValues, SourceInfo } from 'react-number-format'
import cn from 'classnames'
import styles from 'components/atoms/inputs/Input/Input.styl'
import * as Switch from '@radix-ui/react-switch'
import * as Checkbox from '@radix-ui/react-checkbox'
import * as RadioGroup from '@radix-ui/react-radio-group'
import { useFullWidth } from 'hooks/useClassNames'
import { Select } from 'components/atoms/inputs/Select/Select'
import { Option } from 'components/atoms/inputs/Select/parts/types'
import { LabeledOption } from 'components/atoms/inputs/Input/InputOption'
import { isNullOrWhitespace } from 'helpers/utilsHelpers'

export type InputBasicType =
  | 'bank_account'
  | 'checkbox'
  | 'credit_card'
  | 'currency'
  | 'email'
  | 'number'
  | 'password'
  | 'percent'
  | 'phone'
  | 'routing'
  | 'text'
  | 'zip_code'
  | 'ssn'
  | 'switch'
  | 'year'
  | 'month'
  | 'expiry'
  | 'verification_code'

export type InputWithOptionsType = 'radio' | 'select' | 'checkboxes'

export type BaseInputProps =
  & Pick<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onBlur'>
  & {
    name: string
    disabled?: boolean
    fullWidth?: boolean
    innerRef?: RefCallBack
    className?: string
    qaMarker?: string
    onFocus?: () => void
    onEnter?: () => void
    onEscape?: () => void
  }

export type InputWithoutOptionsProps =
  & Pick<InputHTMLAttributes<HTMLInputElement>, 'placeholder' | 'inputMode' | 'maxLength' | 'autoComplete'>
  & BaseInputProps
  & {
    type?: InputBasicType
  }

export type InputWithOptionsProps<OptionValue extends string> = BaseInputProps & {
  type: InputWithOptionsType
  options: Option<OptionValue>[]
}

export type InputProps<OptionValue extends string> = InputWithoutOptionsProps | InputWithOptionsProps<OptionValue>


/**
 * Catch-all Input component that is controlled by react-hook-form
 * Pass onChange, value, innerRef to override react-hook-form control
 *
 ** Probably should not be used directly.
 ** Use LabeledInput instead. Or nest this inside InputLayout
 *
 * @param {string} name - The only required param. Allows react-hook-form to track this field
 * @param {InputType} type - Defaults to text input
 * @requires react-hook-form
 */
export const Input = <OptionValue extends string>(props: InputProps<OptionValue>): ReactElement | null => {
  const { name, type, onEnter, onEscape, fullWidth = true, className: propsClassName, qaMarker, onChange, innerRef, ...otherProps } = props
  const { control, setValue } = useFormContext()
  const { field } = useController({ name, control })
  const { ref, ...fieldProps } = field
  const fullWidthClassName = useFullWidth(fullWidth)
  const className = cn(propsClassName, styles.input, fullWidthClassName)
  const commonProps = useMemo(() => ({
    ...fieldProps,
    ...otherProps,
    className,
    'data-lpignore': 'true',
    'data-qa-type': 'input',
    'data-qa-marker': qaMarker,
  }), [fieldProps, otherProps, className, qaMarker])

  const handleToggle = useCallback((checked: boolean) => {
    setValue(name, checked)
  }, [name, setValue])

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !!onEnter) {
      event.preventDefault()
      onEnter()
    }
    if (event.key === 'Escape' && !!onEscape) {
      event.preventDefault()
      onEscape()
    }
  }, [onEnter, onEscape])

  const setInputRef = useCallback((node: HTMLInputElement | null) => {
    ref(node)
    if (innerRef) {
      innerRef(node)
    }
  }, [innerRef, ref])

  const setToggleRef = useCallback((node: HTMLButtonElement | null) => {
    ref(node)
    if (innerRef) {
      innerRef(node)
    }
  }, [innerRef, ref])

  const setNumberFormatInputRef = useCallback((node: HTMLInputElement) => {
    setInputRef(node)
  }, [setInputRef])

  const handleNumberChange = useCallback(({ value }: NumberFormatValues, { event }: SourceInfo) => {
    if (!fieldProps.onChange) {
      return
    }

    if (!!onChange) {
      onChange({ ...event, target: { ...event.target, value }})
    }

    return fieldProps.onChange({ ...event, target: { ...event.target, value }})
  }, [fieldProps])

  const customHandlerProps = {
    onValueChange: handleNumberChange,
    onChange: undefined,
  }

  /**========================================================================
 *                           NUMBER FIELDS
 *========================================================================**/
  const textFieldProps = useMemo(() => ({
    ...commonProps,
    onKeyDown: handleKeyDown,
  }), [commonProps, handleKeyDown])

  const numberFieldProps: NumberFormatProps = {
    ...textFieldProps,
    type: 'tel',
    inputMode: 'numeric',
    mask: '_',
  }

  if (type === 'ssn') {
    return <NumberFormat { ...textFieldProps } getInputRef={ setNumberFormatInputRef } type='password' inputMode='numeric' maxLength={ 9 } autoComplete='off' />

  } else if (type === 'number') {
    return <NumberFormat { ...numberFieldProps } getInputRef={ setNumberFormatInputRef } mask={ undefined } thousandSeparator />

  } else if (type === 'year') {
    const yearLimit = ({ floatValue }: NumberFormatValues) => !!floatValue && floatValue <= 2050
    return <NumberFormat { ...numberFieldProps } getInputRef={ setNumberFormatInputRef } format='####' isAllowed={ yearLimit } />

  } else if (type === 'month') {
    const monthLimit = ({ floatValue }: NumberFormatValues) => !!floatValue && floatValue <= 12
    return <NumberFormat { ...numberFieldProps } getInputRef={ setNumberFormatInputRef } format='##' isAllowed={ monthLimit } />

  } else if (type === 'expiry') {
    return <NumberFormat { ...numberFieldProps } getInputRef={ setNumberFormatInputRef } format='##/##' placeholder='MM/YY' />

  } else if (type === 'phone') {
    return <NumberFormat { ...numberFieldProps } getInputRef={ setNumberFormatInputRef } format='+1 (###) ###-####' allowEmptyFormatting />

  } else if (type === 'verification_code') {
    return <NumberFormat { ...numberFieldProps } getInputRef={ setNumberFormatInputRef } format='####' />

  } else if (type === 'zip_code') {
    return <NumberFormat { ...numberFieldProps } getInputRef={ setNumberFormatInputRef } format='#####' />

  } else if (type === 'routing') {
    return <NumberFormat { ...numberFieldProps } getInputRef={ setNumberFormatInputRef } format='#########' />

  } else if (type === 'bank_account') {
    return <NumberFormat { ...numberFieldProps } getInputRef={ setNumberFormatInputRef } maxLength={ 17 } />

  } else if (type === 'credit_card') {
    return <NumberFormat { ...numberFieldProps } getInputRef={ setNumberFormatInputRef } format='#### #### #### ####' { ...customHandlerProps } />

  } else if (type === 'percent') {
    const isPercentAllowed = ({ floatValue, value }: NumberFormatValues) => (
      isNullOrWhitespace(value) || floatValue === undefined || floatValue <= 100
    )
    return (
      <NumberFormat
        { ...numberFieldProps }
        getInputRef={ setNumberFormatInputRef }
        mask={ undefined }
        suffix='%'
        decimalScale={ 2 }
        inputMode='decimal'
        isAllowed={ isPercentAllowed }
      />
    )

  } else if (type === 'currency') {
    return (
      <NumberFormat
        { ...numberFieldProps }
        { ...customHandlerProps }
        getInputRef={ setNumberFormatInputRef }
        prefix='$'
        inputMode='decimal'
        thousandSeparator
        decimalScale={ 2 }
        maxLength={ 12 }
        onValueChange={ handleNumberChange }
      />
    )
  }


  /**========================================================================
 *                           OPTION FIELDS
 *========================================================================**/
  const renderOptions = (options: Option<OptionValue>[], optionType: 'checkboxes' | 'radio') => {
    return options.map((option: Option<OptionValue>, idx: number) => (
      <LabeledOption key={ idx } type={ optionType } option={ option } { ...commonProps } />
    ), [commonProps, props, optionType])
  }

  if (props.type === 'radio') {
    return (
      <RadioGroup.Root { ...fieldProps } { ...otherProps } onValueChange={ field.onChange }>
        {renderOptions(props.options, props.type)}
      </RadioGroup.Root>
    )

  } else if (props.type === 'checkboxes') {
    return (
      <>
        {renderOptions(props.options, props.type)}
      </>
    )

  } else if (props.type === 'select') {
    return <Select<OptionValue> { ...fieldProps } options={ props.options } />

  }

  /**========================================================================
 *                           TOGGLE FIELDS
 *========================================================================**/
  if (type === 'checkbox') {
    return (
      <Checkbox.Root ref={ setToggleRef } { ...commonProps } { ...fieldProps } checked={ field.value } onCheckedChange={ handleToggle }>
        <Checkbox.Indicator />
      </Checkbox.Root>
    )

  } else if (type === 'switch') {
    return (
      <Switch.Root ref={ setToggleRef } { ...commonProps } { ...fieldProps } checked={ field.value } onCheckedChange={ handleToggle }>
        <Switch.Thumb />
      </Switch.Root>
    )
  }

  /**========================================================================
 *                           TEXT FIELDS
 *========================================================================**/
  if (type === 'password') {
    return <NumberFormat { ...textFieldProps } getInputRef={ setNumberFormatInputRef } type='password' />
  }

  return <input ref={ setInputRef } type={ type || 'text' } { ...textFieldProps } />
}

export default Input
